import { Component, ErrorInfo, ReactNode } from 'react';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  name?: string; // For identifying which boundary caught the error
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const where = this.props.name ? `: ${this.props.name}` : '';
    console.error(`[ErrorBoundary${where}] Caught error:`, error, errorInfo);

    // The component stack is the only part that names WHICH component blew up,
    // and React populates it even in a production build. Logged on its own line
    // because inside the errorInfo object it collapses to "Object" in devtools
    // and gets missed — which is exactly what happened chasing React #185.
    if (errorInfo?.componentStack) {
      console.error(`[ErrorBoundary${where}] Component stack:${errorInfo.componentStack}`);
    }

    // React encodes production errors as a number and a URL. Print the decoded
    // meaning for the ones that actually recur here, so a minified build still
    // says something useful without a rebuild.
    const code = /Minified React error #(\d+)/.exec(error?.message ?? '')?.[1];
    const KNOWN: Record<string, string> = {
      '185': 'Maximum update depth exceeded — a setState is running on every render (check effect deps and unstable selector/memo results).',
      '310': 'Rendered more hooks than during the previous render — a hook is behind a conditional or an early return.',
      '300': 'Rendered fewer hooks than expected — same cause as #310, other direction.',
      '321': 'Invalid hook call — hooks outside a component, or duplicate React copies.',
    };
    if (code && KNOWN[code]) {
      console.error(`[ErrorBoundary${where}] React #${code}: ${KNOWN[code]}`);
    }

    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="error-boundary-fallback">
          <h3>Something went wrong</h3>
          <p>{this.state.error?.message}</p>
          <button onClick={this.handleRetry}>Try Again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
